import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Search,
  Globe2,
  ShieldCheck,
  Zap,
  Users,
  FileSearch,
  Factory,
  Wrench,
  Cog,
  Layers,
  Truck,
  CheckCircle2,
  Hammer,
  Flame,
  Droplet,
} from "lucide-react";

import WorldMap from "@/components/site/WorldMap";
import Seo, { ORG_LD, WEBSITE_LD } from "@/components/site/Seo";
import { RevealLines, RevealText } from "@/components/site/RevealText";


const CATEGORIES = [
  { icon: Cog, title: "Industrial Equipment & Components", body: "Pumps, valves, mechanical equipment, engineered components and industrial systems.",
    img: "/categories/industrial-equipment.jpg" },
  { icon: Layers, title: "Metals, Tubes & Engineered Materials", body: "Specialty metals, stainless steel, nickel alloys, tubes, pipes and fabricated materials.",
    img: "/categories/metals-tubes.jpg" },
  { icon: Factory, title: "Oil & Gas Supply", body: "Equipment, components, MRO products and specialized materials for energy infrastructure.",
    img: "/categories/oil-gas.jpg" },
  { icon: Zap, title: "Electrical & Mechanical Components", body: "Industrial electrical, mechanical and electromechanical components.",
    img: "/categories/electrical-mechanical.jpg" },
  { icon: Wrench, title: "Custom Manufacturing", body: "Products manufactured to drawings, specifications, samples or technical requirements.",
    img: "/categories/custom-manufacturing.jpg" },
  { icon: Search, title: "Hard-to-Find & Obsolete Parts", body: "Products that are discontinued, difficult to locate or unavailable through normal channels.",
    img: "/categories/hard-to-find.jpg" },
  { icon: Hammer, title: "Forgings", body: "Closed-die, open-die and ring-rolled forgings — from wellhead billets to structural flanges.",
    img: "/categories/forgings.jpg" },
  { icon: Flame, title: "Castings", body: "Sand casting and investment casting — from valve bodies to precision impellers.",
    img: "/categories/castings.jpg" },
  { icon: Droplet, title: "Injection Molding", body: "Plastic injection moulding (PIM) and metal injection moulding (MIM) for high-volume, complex parts.",
    img: "/categories/injection-molding.jpg" },
];

const WHY = [
  { n: "01", title: "Global Supplier Access", body: "Access qualified manufacturers and suppliers across global best-cost markets — India, China, Southeast Asia, the Middle East, Europe and North America." },
  { n: "02", title: "Technical Understanding", body: "We understand specifications, drawings, materials, standards and industrial requirements — so nothing is lost in translation between buyer and factory." },
  { n: "03", title: "Supplier Qualification", body: "We evaluate supplier capability, experience, quality systems, capacity, and commercial reliability before a single quotation reaches you." },
  { n: "04", title: "Speed to Solution", body: "We move quickly from requirement to qualified supplier options and competitive quotations — urgent requirements are same-day scoped." },
  { n: "05", title: "One Accountable Partner", body: "One point of contact from sourcing and supplier identification through procurement, quality, and delivery coordination." },
];

const PROCESS = [
  { n: "01", title: "Tell Us What You Need", body: "Submit your requirement — specification, drawing, part number or product description." },
  { n: "02", title: "We Search Globally", body: "Our sourcing team identifies suitable manufacturers and suppliers across relevant global markets." },
  { n: "03", title: "We Qualify the Options", body: "We assess technical capability, quality, experience, capacity and commercial suitability." },
  { n: "04", title: "You Receive the Right Options", body: "We present competitive supplier and quotation options based on your requirements." },
  { n: "05", title: "We Support the Execution", body: "We coordinate procurement, quality checks, logistics and delivery support." },
];

const INDUSTRIES = [
  "Oil & Gas",
  "Energy",
  "Industrial Manufacturing",
  "Engineering & EPC",
  "Mining & Minerals",
  "Infrastructure",
  "Chemicals & Petrochemicals",
  "General Industrial Procurement",
];

const VERIFICATION = [
  "Technical capability",
  "Manufacturing capacity",
  "Quality systems",
  "Relevant certifications",
  "Industry experience",
  "Product traceability",
  "Commercial reliability",
  "Lead time",
  "Export capability",
];

const FEATURED = [
  "Stainless Steel Products",
  "Nickel Alloys",
  "Inconel Products",
  "Industrial Tubes & Pipes",
  "Flanges & Forgings",
  "Valves",
  "Pumps",
  "Electrical Components",
  "Mechanical Components",
  "Custom Engineered Products",
  "Oil & Gas Equipment",
  "MRO Products",
];


export default function Home() {
  return (
    <div className="bg-nx-bg text-nx-ink" data-testid="page-home">
      <Seo
        title="Global Sourcing Partner for Difficult Industrial Requirements"
        description="NEXORIQ is a UAE-based global sourcing company helping industrial buyers source difficult-to-find materials, components, equipment and specialized products through a qualified worldwide supplier network."
        path="/"
        structuredData={[
          ORG_LD,
          WEBSITE_LD,
          {
            "@context": "https://schema.org",
            "@type": "Service",
            provider: { "@type": "Organization", name: "NEXORIQ FZ LLC" },
            serviceType: "Global industrial sourcing",
            areaServed: "Worldwide",
            description:
              "NEXORIQ helps industrial companies find qualified suppliers globally for difficult, technically complex, urgent and specialized sourcing requirements.",
          },
        ]}
      />
      {/* ============ 01 · HERO (Navy) ============ */}
      <section className="relative min-h-[92vh] flex items-center bg-nx-navydark text-nx-bg overflow-hidden" data-testid="hero">
        {/* Faint world map behind headline */}
        <div className="absolute inset-0 opacity-[0.22] text-nx-bg pointer-events-none">
          <WorldMap className="absolute inset-0 h-full" />
        </div>
        {/* Vignette */}
        <div className="absolute inset-0 bg-gradient-to-b from-nx-navydark/40 via-nx-navydark/60 to-nx-navydark" />

        <div className="relative z-10 w-full px-6 md:px-12 lg:px-20 pt-32 md:pt-40 pb-20">
          <div className="flex items-center gap-3 font-mono text-[0.65rem] uppercase tracking-[0.28em] text-nx-copper mb-10" data-testid="hero-eyebrow">
            <span className="h-px w-10 bg-nx-copper" />
            NEXORIQ FZ LLC · UAE · Global Sourcing
          </div>

          <h1
            className="font-cabinet font-black tracking-[-0.045em] leading-[0.92] text-[13vw] md:text-[8.4vw] lg:text-[7.4vw] max-w-[16ch]"
            data-testid="hero-title"
          >
            Difficult to source?
            <br />
            <span className="text-nx-copper">We find the way.</span>
          </h1>

          <p
            className="mt-10 max-w-2xl text-lg md:text-xl leading-relaxed text-nx-bg/80"
            data-testid="hero-subtitle"
          >
            NEXORIQ helps industrial companies source difficult-to-find materials, components, equipment
            and specialized products through a qualified global supplier network.
          </p>

          <div className="mt-12 flex flex-wrap items-center gap-5" data-testid="hero-ctas">
            <Link to="/sourcing-request" className="nx-btn on-dark" data-testid="hero-cta-primary">
              Submit a Sourcing Requirement <ArrowUpRight size={14} />
            </Link>
            <Link to="/capabilities" className="nx-btn on-dark ghost" data-testid="hero-cta-secondary">
              Explore Our Capabilities
            </Link>
          </div>

          {/* Bottom strip */}
          <div className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 max-w-4xl">
            {[
              ["Global", "Supplier network across best-cost regions"],
              ["Technical", "Engineer-led specification review"],
              ["Fast", "Same-day response on urgent scope"],
              ["Accountable", "One partner, requirement to delivery"],
            ].map(([k, v]) => (
              <div key={k} className="border-t border-nx-bg/25 pt-4" data-testid={`hero-pillar-${k.toLowerCase()}`}>
                <div className="font-cabinet font-black text-xl md:text-2xl tracking-tight text-nx-copper">{k}</div>
                <div className="mt-1 font-mono text-[0.62rem] uppercase tracking-[0.18em] text-nx-bg/60 leading-relaxed">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 02 · THE SOURCING PROBLEM (Off-white) ============ */}
      <section className="bg-nx-bg text-nx-ink px-6 md:px-12 lg:px-20 py-24 md:py-32" data-testid="section-problem">
        <SectionOverline n="02" label="The Sourcing Problem" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-8">
          <div className="lg:col-span-6">
            <RevealLines
              as="h2"
              lines={[<>Some requirements are</>, <>harder than others.</>]}
              className="font-cabinet font-black tracking-[-0.035em] leading-[0.95] text-[10vw] md:text-[5.2vw] lg:text-[4.8vw] text-nx-navy"
            />
          </div>
          <div className="lg:col-span-6 lg:pt-4">
            <p className="text-nx-navy2 text-lg leading-relaxed max-w-xl">
              Every procurement team runs into requirements where standard channels fall short — an
              obsolete part, an unfamiliar geography, a supplier who won't return calls, a specification
              that needs an engineer to read it properly.
            </p>
            <ul className="mt-10 space-y-4 border-t border-nx-navy/20">
              {[
                "Finding the right supplier for a specialized item",
                "Sourcing obsolete or hard-to-find parts",
                "Finding manufacturers in unfamiliar countries",
                "Verifying supplier capability before you commit",
                "Managing international communication and paperwork",
                "Achieving a competitive landed cost, not just factory price",
                "Meeting urgent delivery requirements without cutting corners",
                "Handling technically complex products with confidence",
              ].map((line, i) => (
                <li key={line} className="grid grid-cols-[auto_1fr] gap-4 py-4 border-b border-nx-navy/15 group" data-testid={`problem-item-${i}`}>
                  <span className="font-mono text-[0.62rem] uppercase tracking-[0.24em] text-nx-copper pt-1.5">→</span>
                  <span className="text-nx-navy leading-relaxed">{line}</span>
                </li>
              ))}
            </ul>
            <p className="mt-10 font-cabinet font-black text-2xl md:text-3xl tracking-tight text-nx-navy leading-tight max-w-lg">
              That's where NEXORIQ comes in — an extension of your procurement and supply chain team.
            </p>
          </div>
        </div>
      </section>

      {/* ============ 03 · WHAT WE SOURCE (Navy) ============ */}
      <section className="bg-nx-navydark text-nx-bg px-6 md:px-12 lg:px-20 py-24 md:py-32" data-testid="section-what-we-source">
        <SectionOverline n="03" label="What We Source" dark />
        <div className="mt-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <h2 className="font-cabinet font-black tracking-[-0.035em] leading-[0.95] text-[10vw] md:text-[5vw] lg:text-[4.4vw] text-nx-bg max-w-3xl">
            Nine categories.<br />One accountable partner.
          </h2>
          <Link to="/what-we-source" className="inline-flex items-center gap-2 font-mono text-[0.7rem] uppercase tracking-[0.24em] text-nx-copper hover:text-nx-bg transition-colors" data-testid="what-we-source-link">
            See full catalogue <ArrowUpRight size={12} />
          </Link>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-nx-bg/15 border border-nx-bg/15" data-testid="what-we-source-grid">
          {CATEGORIES.map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              className="bg-nx-navydark group hover:bg-nx-navy transition-colors overflow-hidden flex flex-col"
              data-testid={`category-${i}`}
            >
              <div className="relative aspect-[16/9] overflow-hidden bg-nx-navy">
                <img
                  src={c.img}
                  alt={c.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-nx-navydark via-nx-navydark/40 to-transparent" />
                <c.icon size={22} className="absolute top-4 left-4 text-nx-copper" />
              </div>
              <div className="p-8 md:p-10">
                <h3 className="font-cabinet font-black text-2xl tracking-tight text-nx-bg leading-tight">{c.title}</h3>
                <p className="mt-4 text-nx-bg/70 leading-relaxed text-sm">{c.body}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ============ 04 · WHY NEXORIQ (Off-white) ============ */}
      <section className="bg-nx-bg text-nx-ink px-6 md:px-12 lg:px-20 py-24 md:py-32" data-testid="section-why">
        <SectionOverline n="04" label="Why NEXORIQ" />
        <h2 className="mt-8 font-cabinet font-black tracking-[-0.035em] leading-[0.95] text-[10vw] md:text-[5vw] lg:text-[4.4vw] text-nx-navy max-w-4xl">
          More than a supplier search.<br />
          <span className="text-nx-copper">A sourcing partner.</span>
        </h2>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 md:gap-10">
          {WHY.map((v, i) => (
            <motion.div
              key={v.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className="border-t border-nx-navy pt-5"
              data-testid={`why-${v.n}`}
            >
              <div className="font-mono text-[0.62rem] uppercase tracking-[0.24em] text-nx-copper mb-3">
                {v.n} · Value
              </div>
              <h3 className="font-cabinet font-black text-xl tracking-tight text-nx-navy leading-tight">{v.title}</h3>
              <p className="mt-3 text-nx-navy2 text-sm leading-relaxed">{v.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ============ 05 · HOW IT WORKS (Navy) ============ */}
      <section className="bg-nx-navydark text-nx-bg px-6 md:px-12 lg:px-20 py-24 md:py-32" data-testid="section-process">
        <SectionOverline n="05" label="How It Works" dark />
        <h2 className="mt-8 font-cabinet font-black tracking-[-0.035em] leading-[0.95] text-[10vw] md:text-[5vw] lg:text-[4.4vw] text-nx-bg max-w-4xl">
          From requirement to delivered product.
        </h2>

        <div className="mt-16 relative">
          <div className="absolute left-0 right-0 top-16 h-px bg-nx-bg/20 hidden md:block" />
          <div className="grid grid-cols-1 md:grid-cols-5 gap-10 md:gap-6">
            {PROCESS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.55, delay: i * 0.1 }}
                className="relative"
                data-testid={`process-${s.n}`}
              >
                <div className="w-4 h-4 bg-nx-copper mb-8 relative z-10" />
                <div className="font-mono text-[0.62rem] uppercase tracking-[0.24em] text-nx-copper mb-2">Step {s.n}</div>
                <h3 className="font-cabinet font-black text-2xl tracking-tight text-nx-bg leading-tight">{s.title}</h3>
                <p className="mt-3 text-nx-bg/70 text-sm leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 06 · INDUSTRIES (Off-white) ============ */}
      <section className="bg-nx-bg text-nx-ink px-6 md:px-12 lg:px-20 py-24 md:py-32" data-testid="section-industries">
        <SectionOverline n="06" label="Industries We Serve" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-8">
          <div className="lg:col-span-5">
            <h2 className="font-cabinet font-black tracking-[-0.035em] leading-[0.95] text-[10vw] md:text-[5vw] lg:text-[4.4vw] text-nx-navy">
              Wherever the requirement is technical.
            </h2>
            <p className="mt-8 text-nx-navy2 max-w-md leading-relaxed">
              NEXORIQ is built to serve buyers whose sourcing is routine some months and impossible others.
              The industries below are where we are most often called in.
            </p>
            <Link to="/industries" className="mt-8 inline-flex items-center gap-2 font-mono text-[0.7rem] uppercase tracking-[0.24em] text-nx-copper hover:text-nx-navy transition-colors" data-testid="industries-link">
              Deep-dive by industry <ArrowUpRight size={12} />
            </Link>
          </div>
          <div className="lg:col-span-7">
            <div className="border-t border-nx-navy">
              {INDUSTRIES.map((ind, i) => (
                <Link
                  key={ind}
                  to="/industries"
                  className="group grid grid-cols-[auto_1fr_auto] items-center gap-6 py-6 border-b border-nx-navy/20 hover:bg-nx-bg2 transition-colors px-2"
                  data-testid={`industry-${i}`}
                >
                  <span className="font-mono text-[0.62rem] uppercase tracking-[0.24em] text-nx-copper">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-cabinet font-black text-xl md:text-2xl tracking-tight text-nx-navy leading-tight group-hover:text-nx-copper transition-colors">
                    {ind}
                  </span>
                  <ArrowUpRight size={16} className="text-nx-navy group-hover:text-nx-copper group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ 07 · GLOBAL SOURCING NETWORK (Navy) ============ */}
      <section className="bg-nx-navydark text-nx-bg px-6 md:px-12 lg:px-20 py-24 md:py-32 overflow-hidden" data-testid="section-network">
        <SectionOverline n="07" label="Global Sourcing Network" dark />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-8">
          <div className="lg:col-span-5">
            <h2 className="font-cabinet font-black tracking-[-0.035em] leading-[0.95] text-[10vw] md:text-[5vw] lg:text-[4.4vw] text-nx-bg">
              One requirement.<br />
              <span className="text-nx-copper">A world of possibilities.</span>
            </h2>
            <p className="mt-8 text-nx-bg/70 leading-relaxed max-w-md">
              We work through a qualified supplier network across best-cost manufacturing regions.
              Every requirement is matched to the geography and supplier best suited to it — not the
              other way around.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-3">
              {["India", "China", "Southeast Asia", "Middle East", "Europe", "North America"].map((geo) => (
                <div key={geo} className="flex items-center gap-3 font-mono text-[0.7rem] uppercase tracking-[0.22em] text-nx-bg/80" data-testid={`geo-${geo.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                  <span className="w-1.5 h-1.5 bg-nx-copper" />
                  {geo}
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7 relative text-nx-bg">
            <WorldMap className="opacity-90" showLabels />
          </div>
        </div>
      </section>

      {/* ============ 08 · QUALITY & VERIFICATION (Off-white) ============ */}
      <section className="bg-nx-bg text-nx-ink px-6 md:px-12 lg:px-20 py-24 md:py-32" data-testid="section-quality">
        <SectionOverline n="08" label="Quality & Supplier Verification" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-8">
          <div className="lg:col-span-6">
            <h2 className="font-cabinet font-black tracking-[-0.035em] leading-[0.95] text-[10vw] md:text-[5vw] lg:text-[4.4vw] text-nx-navy">
              The right supplier is more than the right price.
            </h2>
            <p className="mt-8 text-nx-navy2 leading-relaxed max-w-lg">
              A cheap quote from an unqualified factory is not a saving — it's a risk. Every supplier
              we present to a client is qualified against the criteria on the right, before a single
              number is exchanged.
            </p>
          </div>
          <div className="lg:col-span-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-nx-navy/20 border border-nx-navy/25">
              {VERIFICATION.map((v, i) => (
                <div key={v} className="bg-nx-bg p-6 flex items-start gap-4" data-testid={`verify-${i}`}>
                  <CheckCircle2 size={18} className="text-nx-copper flex-shrink-0 mt-0.5" />
                  <span className="text-nx-navy leading-relaxed">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ 09 · FEATURED CATEGORIES (Navy) ============ */}
      <section className="bg-nx-navydark text-nx-bg px-6 md:px-12 lg:px-20 py-24 md:py-32" data-testid="section-featured">
        <SectionOverline n="09" label="Featured Sourcing Categories" dark />
        <h2 className="mt-8 font-cabinet font-black tracking-[-0.035em] leading-[0.95] text-[10vw] md:text-[5vw] lg:text-[4.4vw] text-nx-bg max-w-4xl">
          Materials & products we source often.
        </h2>
        <p className="mt-6 text-nx-bg/70 leading-relaxed max-w-xl">
          This list is a snapshot, not a limit. If your requirement is not listed, it is likely still
          something we can source — send it across and we'll respond.
        </p>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-nx-bg/15 border border-nx-bg/15" data-testid="featured-grid">
          {FEATURED.map((f, i) => (
            <div key={f} className="bg-nx-navydark p-6 md:p-8 flex flex-col justify-between min-h-[130px] group hover:bg-nx-navy transition-colors" data-testid={`featured-${i}`}>
              <span className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-nx-copper">{String(i + 1).padStart(2, "0")}</span>
              <span className="font-cabinet font-black text-lg md:text-xl tracking-tight text-nx-bg leading-tight mt-6 group-hover:text-nx-copper transition-colors">
                {f}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ============ 10 · TRUST / PROOF (Off-white) ============ */}
      <section className="bg-nx-bg text-nx-ink px-6 md:px-12 lg:px-20 py-24 md:py-32" data-testid="section-trust">
        <SectionOverline n="10" label="Trust & Credibility" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-8">
          <div className="lg:col-span-5">
            <h2 className="font-cabinet font-black tracking-[-0.035em] leading-[0.95] text-[10vw] md:text-[5vw] lg:text-[4.4vw] text-nx-navy">
              Built for industrial buyers.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
              {[
                ["UAE-based", "Registered in Al Hulaila Industrial Zone-FZ, Ras Al Khaimah, UAE."],
                ["Global reach", "Qualified supplier network across best-cost manufacturing regions."],
                ["Engineer-led", "Sourcing decisions made by people who read drawings."],
                ["Multi-country experience", "Buyers in the GCC, Europe, and North America."],
                ["Technical procurement", "Comfortable with specifications, standards and drawings."],
                ["Discreet", "We work behind the scenes for procurement teams."],
              ].map(([k, v], i) => (
                <div key={k} className="border-t border-nx-navy/25 pt-4" data-testid={`trust-${i}`}>
                  <div className="font-cabinet font-black text-lg tracking-tight text-nx-navy">{k}</div>
                  <div className="mt-1 text-sm text-nx-navy2 leading-relaxed">{v}</div>
                </div>
              ))}
            </div>

            {/* Case studies placeholder */}
            <div className="mt-16 border border-dashed border-nx-navy/30 p-8 md:p-10 bg-nx-bg2/40" data-testid="case-studies-placeholder">
              <div className="font-mono text-[0.62rem] uppercase tracking-[0.24em] text-nx-copper mb-3">Case Studies · Coming soon</div>
              <p className="text-nx-navy leading-relaxed max-w-2xl">
                We are preparing a set of anonymised sourcing case studies — the challenge, what we
                did, and the result. Reach out if you'd like a walk-through of relevant references
                for your industry.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 10.5 · REASSURANCE (Off-white → Navy split) ============ */}
      <section className="bg-nx-bg2 text-nx-ink px-6 md:px-12 lg:px-20 py-20 md:py-24" data-testid="section-reassurance">
        <div className="font-mono text-[0.65rem] uppercase tracking-[0.28em] text-nx-copper mb-6">A quick note</div>
        <h2 className="font-cabinet font-black tracking-[-0.035em] leading-[0.95] text-[9vw] md:text-[4.4vw] lg:text-[3.6vw] text-nx-navy max-w-4xl">
          How we treat both sides of the deal.
        </h2>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.6 }}
            className="bg-nx-bg border border-nx-navy/20 p-8 md:p-10 relative"
            data-testid="reassurance-factories"
          >
            <span className="absolute top-0 left-0 h-1 w-16 bg-nx-copper" />
            <div className="font-mono text-[0.6rem] uppercase tracking-[0.24em] text-nx-copper mb-4">For manufacturers</div>
            <h3 className="font-cabinet font-black text-2xl md:text-3xl tracking-tight text-nx-navy leading-tight">
              Long-term allies, not an open directory.
            </h3>
            <p className="mt-5 text-nx-navy2 leading-relaxed">
              We do not operate as an open directory. We treat our manufacturing partners as
              long-term allies — offering clear engineering drawings, predictable RFQ pipelines and
              prompt payment terms.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.6, delay: 0.08 }}
            className="bg-nx-navydark text-nx-bg p-8 md:p-10 relative"
            data-testid="reassurance-buyers"
          >
            <span className="absolute top-0 left-0 h-1 w-16 bg-nx-copper" />
            <div className="font-mono text-[0.6rem] uppercase tracking-[0.24em] text-nx-copper mb-4">For buyers</div>
            <h3 className="font-cabinet font-black text-2xl md:text-3xl tracking-tight text-nx-bg leading-tight">
              Every component clears a 4-step audit.
            </h3>
            <p className="mt-5 text-nx-bg/80 leading-relaxed">
              Every component NEXORIQ delivers originates from a factory that has completed our
              four-step technical and quality audit — capability, compliance, capacity and
              commercial reliability.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ============ 11 · ABOUT (Navy) ============ */}
      <section className="bg-nx-navydark text-nx-bg px-6 md:px-12 lg:px-20 py-24 md:py-32" data-testid="section-about">
        <SectionOverline n="11" label="About NEXORIQ" dark />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-8">
          <div className="lg:col-span-7">
            <h2 className="font-cabinet font-black tracking-[-0.035em] leading-[0.95] text-[10vw] md:text-[5vw] lg:text-[4.4vw] text-nx-bg">
              A global sourcing partner built for difficult requirements.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-6">
            <p className="text-nx-bg/80 leading-relaxed text-lg">
              NEXORIQ was created to help companies overcome the complexity of global sourcing.
            </p>
            <p className="mt-5 text-nx-bg/70 leading-relaxed">
              We connect industrial buyers with capable suppliers across global manufacturing markets —
              combining sourcing expertise, technical understanding and commercial discipline to help
              customers find the right solution, not just the first one.
            </p>
            <Link to="/about" className="mt-8 inline-flex items-center gap-2 font-mono text-[0.7rem] uppercase tracking-[0.24em] text-nx-copper hover:text-nx-bg transition-colors" data-testid="about-link">
              About the company <ArrowUpRight size={12} />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ 12 · FINAL CTA (Copper) ============ */}
      <section className="bg-nx-copper text-nx-bg px-6 md:px-12 lg:px-20 py-24 md:py-32 relative overflow-hidden" data-testid="section-final-cta">
        <div className="absolute inset-0 opacity-10 text-nx-bg pointer-events-none">
          <WorldMap className="absolute inset-0 h-full" />
        </div>
        <div className="relative z-10 max-w-5xl">
          <div className="font-mono text-[0.7rem] uppercase tracking-[0.28em] text-nx-bg/80 mb-6" data-testid="final-cta-eyebrow">
            12 · The next step
          </div>
          <h2 className="font-cabinet font-black tracking-[-0.045em] leading-[0.92] text-[13vw] md:text-[7.4vw] lg:text-[6vw] text-nx-bg">
            Have a difficult sourcing requirement?
          </h2>
          <p className="mt-8 max-w-xl text-lg text-nx-bg/85 leading-relaxed">
            Send us the requirement. We will help you find the right path — with the right supplier,
            the right price, and the right timeline.
          </p>
          <div className="mt-12 flex flex-wrap items-center gap-5">
            <Link to="/sourcing-request" className="inline-flex items-center gap-3 bg-nx-navydark text-nx-bg px-6 py-4 border border-nx-navydark hover:bg-nx-bg hover:text-nx-navydark transition-colors font-mono text-[0.72rem] uppercase tracking-[0.22em]" data-testid="final-cta-primary">
              Submit Your Requirement <ArrowUpRight size={14} />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 border-b border-nx-bg pb-0.5 font-mono text-[0.72rem] uppercase tracking-[0.22em] text-nx-bg hover:text-nx-navydark transition-colors" data-testid="final-cta-secondary">
              Contact Our Sourcing Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}


function SectionOverline({ n, label, dark = false }) {
  return (
    <div className={"flex items-center gap-4 font-mono text-[0.65rem] uppercase tracking-[0.28em] " + (dark ? "text-nx-copper" : "text-nx-copper")}>
      <span className={"w-10 h-px " + (dark ? "bg-nx-copper" : "bg-nx-copper")} />
      <span className="text-nx-copper">{n}</span>
      <span className={dark ? "text-nx-bg/70" : "text-nx-navy2"}>// {label}</span>
    </div>
  );
}
